import { doc, increment, setDoc } from 'firebase/firestore'
import { auth, db } from './firebase'
import { clientKey, dayKey, hourKey, makeMeter } from './usage'

/**
 * The live tally, and where it goes.
 *
 * Held in memory and written out periodically rather than on every snapshot: a write per
 * read would cost far more than the thing being measured. It is flushed when the tab is
 * hidden too, which is what catches somebody closing the laptop.
 *
 * Written with `increment`, so several people and several tabs add to the same day without
 * reading it first and without overwriting each other.
 *
 * The flush write is deliberately not counted. A meter that measures itself climbs on its
 * own, and the number people would then be watching is mostly the watching.
 */
export const meter = makeMeter()

/**
 * A minute, not five.
 *
 * Five minutes was chosen when the only question was what a day cost. It loses everything
 * accrued since the last flush whenever a tab dies without warning — a headless browser
 * killed by the process that started it fires neither `pagehide` nor `visibilitychange` —
 * and a tally lost that way is exactly the one worth having, because an automated browser
 * looping overnight is the case this is for. A minute costs at most sixty flushes an hour
 * per busy tab, and an idle tab with nothing pending writes nothing at all.
 */
const EVERY = 60 * 1000

/**
 * This page load, distinguished from the next one.
 *
 * Generated per load rather than kept in storage on purpose: a reload starts with a cold
 * cache and pays for every document again, so twenty reloads should look like twenty
 * things rather than one busy tab.
 */
const SESSION = Math.random().toString(36).slice(2, 8)

/** Who and which tab, as it is recorded. Exported so the panel can explain a key. */
export const thisClient = () => clientKey(auth.currentUser?.email, SESSION)

export async function flushUsage(): Promise<void> {
  const pending = meter.drain()
  if (pending.reads === 0 && pending.writes === 0 && pending.deletes === 0) return

  // One clock reading for both documents, so an hour's rows always belong to the day's.
  // A tally that straddles an hour boundary lands in the hour it was flushed in, which
  // with a one-minute flush is a minute of slack on a figure read in hours.
  const at = new Date()
  const day = dayKey(at)
  const counters = {
    reads: increment(pending.reads),
    writes: increment(pending.writes),
    deletes: increment(pending.deletes),
  }
  // One counter per collection, merged into the same document. Each leaf increments on
  // its own, so several tabs and several people add up without any of them reading first.
  const asCounters = (counts: Record<string, number>) =>
    Object.fromEntries(Object.entries(counts).map(([name, n]) => [name, increment(n)]))
  const sources = asCounters(pending.sources)
  const writeSources = asCounters(pending.writeSources)
  const who = thisClient()

  try {
    await Promise.all([
      // The day, which is what the trend is read from.
      setDoc(doc(db, 'usage', day), { ...counters, sources, writeSources }, { merge: true }),
      // The hour, which is what a spike is explained from: when it happened, what it
      // read, and which signed-in tab was doing the reading.
      setDoc(
        doc(db, 'usage', day, 'hours', hourKey(at)),
        {
          ...counters, sources, writeSources,
          // Reads and writes are kept as two maps rather than one map of pairs: the
          // hour documents written before this existed hold a number under each tab's
          // key, and turning that key into a map would make every one of them a type
          // conflict the server rejects — losing the whole flush, silently.
          clients: { [who]: increment(pending.reads) },
          clientWrites: { [who]: increment(pending.writes) },
        },
        { merge: true },
      ),
    ])
  } catch {
    // A reader who may not write usage, or an offline tab. Losing a tally is not worth an
    // error anywhere; the next flush carries on from zero.
  }
}

/** Starts the periodic flush. Returns the stop function, for symmetry with a listener. */
export function startUsageReporting(): () => void {
  const timer = setInterval(() => { void flushUsage() }, EVERY)
  const onHide = () => { if (document.visibilityState === 'hidden') void flushUsage() }
  document.addEventListener('visibilitychange', onHide)
  window.addEventListener('pagehide', onHide)
  return () => {
    clearInterval(timer)
    document.removeEventListener('visibilitychange', onHide)
    window.removeEventListener('pagehide', onHide)
    void flushUsage()
  }
}
